import React from 'react'
import dayjs from 'lib/dayjs'
import { useTranslation } from 'lib/i18n'

import Link from 'components/Link/Link'
import { ReviewBox } from 'components/ReviewBox/ReviewBox'
import { ProfilePictureAndName } from 'components/UI/Avatars/Avatars'
import { ModalCard } from 'components/UI/ModalCard/ModalCard'
import Pagination from 'components/UI/Pagination/Pagination'
import { ReviewStats } from 'components/UI/Reviews/ReviewStats/ReviewStats'
import { Spinner } from 'components/UI/Spinner/Spinner'
import usePublicReviews from 'hooks/usePublicReviews'
import useRouter from 'hooks/useRouter'

import styles from './ReviewsModal.module.css'

type ReviewsModalProps = {
  showProfile?: boolean
}

const ReviewsModal: React.FC<ReviewsModalProps> = ({ showProfile }) => {
  const REVIEWS_PER_PAGE = 10

  const { t } = useTranslation()

  const router = useRouter()
  const { slug } = router.query

  const queryPage = router.query.page as string
  const page = queryPage ? parseInt(queryPage, 10) : 1

  const {
    name,
    title,
    profilePicture,
    loading,
    reviews = [],
    reviewCountByRating,
    totalCount,
  } = usePublicReviews(page - 1, REVIEWS_PER_PAGE)

  const totalPages = Math.ceil(totalCount / REVIEWS_PER_PAGE)

  const ratings = reviewCountByRating.reduce((acc, { count, rating }) => {
    acc[rating] = count
    return acc
  }, {} as Record<number, number>)

  const imageUrl = profilePicture?.url

  const profileLink = `/profile/${slug}/`

  const navigateBackToProfile = () => router.push(profileLink)

  const navigateToPage = (i: number) => {
    router.push(
      {
        pathname: '/profile/[slug]/',
        query: {
          ...router.query,
          page: i,
        },
      },
      `/profile/${slug}/reviews?page=${i}`,
      { scroll: false },
    )
  }

  return (
    <ModalCard
      disablePageScroll
      onClose={navigateBackToProfile}
      removeCloseButton={showProfile}
    >
      {showProfile && (
        <Link href={profileLink}>
          <a>
            <ProfilePictureAndName
              name={t('ReviewsModal.modalTitle', '{{profileName}} - Reviews', {
                profileName: name,
              })}
              pictureUrl={imageUrl}
              textColor="dark"
              title={title}
              size={60}
              className={styles.profileHeader}
            />
          </a>
        </Link>
      )}

      {loading ? (
        <div className={styles.loading}>
          <Spinner size={50} color="black" />
        </div>
      ) : (
        <>
          <div className={styles.modalContent} data-cy="reviewsModal">
            <ReviewStats className={styles.average} ratings={ratings} />
            {reviews.map(({ id, rating, messageToProfile, reviewedAt }) => (
              <ReviewBox
                className={styles.reviewBox}
                key={id}
                message={messageToProfile}
                rating={rating}
                date={dayjs(reviewedAt).fromNow()}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              navigateCallback={navigateToPage}
            />
          )}
        </>
      )}
    </ModalCard>
  )
}

export default ReviewsModal
